import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(
    () => articles.find((a) => a.slug === slug),
    [slug]
  );

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.categoryId === article.categoryId && a.id !== article.id)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 pb-20 text-center">
          <p className="text-foreground-600">文章不存在</p>
          <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer no-underline mt-2 inline-block">
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.publishedAt,
    publisher: {
      '@type': 'Organization',
      name: '爱妻艺',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://mhowiub.cn/article/${article.slug}`,
    },
  };

  const faqSchemas = [
    {
      title: '结婚纪念日惊喜计划：让妻子感动落泪的7个创意方案',
      slug: 'jinianri-jingxi-jihua',
    },
    {
      title: '夫妻争吵时，高情商丈夫会做的5件事',
      slug: 'qingxu-guanli-fuqi-zhengchao',
    },
    {
      title: '婚姻七年之痒破解法：让爱情永远保鲜的秘诀',
      slug: 'aiqing-xinxian-gan-weichi',
    },
    {
      title: '冲突不是敌人：如何让每一次分歧都让感情升温',
      slug: 'chongtu-jiejue-shengji-ganqing',
    },
  ];

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqSchemas.map((faq) => ({
      '@type': 'Question',
      name: faq.title,
      acceptedAnswer: {
        '@type': 'Answer',
        text: `查看完整文章了解${faq.title}的详细内容`,
      },
    })),
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-20">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: article.categoryName, href: `/category/${article.categorySlug}` },
                  { label: article.title },
                ]}
              />

              {/* Article Header */}
              <article>
                <header className="mb-8">
                  <Link
                    to={`/category/${article.categorySlug}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4 hover:bg-primary-200 transition-colors whitespace-nowrap cursor-pointer no-underline"
                  >
                    {article.categoryName}
                  </Link>
                  <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                    {article.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                    <span className="flex items-center gap-1.5">
                      <i className="ri-user-line"></i>
                      {article.author}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <i className="ri-calendar-line"></i>
                      {article.publishedAt}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <i className="ri-time-line"></i>
                      阅读约{article.readTime}
                    </span>
                  </div>
                </header>

                {/* Cover Image */}
                <div className="rounded-2xl overflow-hidden mb-10 aspect-[16/7]">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    title={`${article.title} | 爱妻艺`}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Article Content */}
                <div
                  className="prose-custom"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                {/* Tags / Share */}
                <div className="border-t border-background-200 mt-12 pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-foreground-500">分享：</span>
                    <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer border-none" aria-label="复制链接">
                      <i className="ri-link"></i>
                    </button>
                    <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer border-none" aria-label="分享到微信">
                      <i className="ri-wechat-line"></i>
                    </button>
                    <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer border-none" aria-label="分享到微博">
                      <i className="ri-weibo-line"></i>
                    </button>
                  </div>
                  <Link
                    to={`/category/${article.categorySlug}`}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 whitespace-nowrap cursor-pointer no-underline"
                  >
                    查看更多{article.categoryName}文章
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </article>

              {/* Related Articles */}
              {relatedArticles.length > 0 && (
                <section className="mt-16">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                    相关推荐
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {relatedArticles.map((related) => (
                      <ArticleCard
                        key={related.id}
                        slug={related.slug}
                        title={related.title}
                        excerpt={related.excerpt}
                        coverImage={related.coverImage}
                        categoryName={related.categoryName}
                        categorySlug={related.categorySlug}
                        author={related.author}
                        publishedAt={related.publishedAt}
                        readTime={related.readTime}
                      />
                    ))}
                  </div>
                </section>
              )}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}